import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, List, ListItem } from '@mui/material'
import useQueryGet from 'hooks/useQueryGet'
import { useState } from 'react'
import { EmbargoDetails, PrizeDrawDetails } from 'types/prize-draw'
import { PrizeAssigned } from './prize-assigned'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { axiosPost } from 'connectors/axiosPost'
import { useAuthContext } from 'context/AuthContext'
import { useAlert } from 'context/AlertContext'
import { RejectButton } from './reject-button'
import { useTranslation } from 'react-i18next'
import { AxiosResponse } from 'axios'
import { format } from 'date-fns'
import { PrizeAssignedEmbargo } from './prize-assigned-embargo'

export type RequestDetailsModalProps = {
  requestId: number
  type: 'prize-embargo' | 'prize-draw'
}

export const RequestDetailsModal = ({ requestId, type }: RequestDetailsModalProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'prizesManagement' })
  const { changeMessage } = useAlert()
  const { token, refreshToken } = useAuthContext()
  const [isOpen, setOpen] = useState(false)
  const queryClient = useQueryClient()

  const close = () => setOpen(p => !p)
  const open = () => setOpen(p => !p)

  const {
    data: request,
    isLoading: isRequestLoading,
    refetch,
  } = useQueryGet<PrizeDrawDetails | EmbargoDetails>({
    queryKey: ['request', `request-${requestId}`],
    endpoint: `${type}/request/${requestId}`,
    queryOptions: { enabled: isOpen },
  })

  const refetchList = () => {
    queryClient.refetchQueries({ queryKey: [`${type}-requests-query`], exact: false })
    refetch()
  }

  const { mutate: sendTestEmails, isLoading: isSendingTests } = useMutation(
    async () =>
      await axiosPost<object | { error: AxiosResponse | undefined }>(
        token,
        refreshToken,
        `${type}/request/emails/${requestId}`,
        {}
      ),
    {
      onSuccess: data => {
        if (data && 'error' in data) {
          changeMessage(`Failure during sending test emails`, 'warning')
        } else {
          changeMessage('Tests emails sent', 'success')
        }
        refetchList()
      },
    }
  )

  const { mutate: sendSampleEmail, isLoading: isSendingSample } = useMutation(
    async () =>
      await axiosPost<object | { error: AxiosResponse | undefined }>(
        token,
        refreshToken,
        `${type}/request/sample/${requestId}`,
        {}
      ),
    {
      onSuccess: data => {
        if (data && 'error' in data) {
          changeMessage(`Failure during sending sample email`, 'warning')
        } else {
          changeMessage('Sample email sent', 'success')
        }
        refetchList()
      },
    }
  )

  const { mutate: sendFinishEmails, isLoading: isSendingFinish } = useMutation(
    async () =>
      await axiosPost<object | { error: AxiosResponse | undefined }>(
        token,
        refreshToken,
        `${type}/request/finish/${requestId}`,
        {}
      ),
    {
      onSuccess: data => {
        if (data && 'error' in data) {
          changeMessage(`Failure during sending finish emails`, 'warning')
        } else {
          changeMessage('Finish emails sent', 'success')
        }
        refetchList()
      },
    }
  )

  const isDisabledButtons = isRequestLoading || isSendingTests || isSendingSample || isSendingFinish

  return (
    <>
      <Button onClick={open}>{t('actions.details')}</Button>

      {isOpen && (
        <Dialog
          open={isOpen}
          onClose={close}
          fullScreen
          PaperProps={{ sx: { margin: 4, height: 'calc(100% - 64px)', borderRadius: 4 } }}
        >
          <DialogTitle>
            {type === 'prize-embargo' ? 'Example applications from request' : t('detailsTitle')}
          </DialogTitle>
          <DialogContent>
            {request !== undefined && request !== null && !('error' in request) && type === 'prize-draw' ? (
              <Box mt={2}>
                <List sx={{ padding: 0 }}>
                  {(request as PrizeDrawDetails).result?.map((item, idx) => (
                    <ListItem key={idx}>
                      <PrizeAssigned
                        item={item}
                        name={`${item.name} ${item.embargoDate ? '( Embargo on ' + format(new Date(item.embargoDate), 'MMM dd, yyyy') + ')' : ''}`}
                        marketName={request.request.market.name}
                        type={type}
                        refetch={refetchList}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            ) : null}
            {request !== undefined && request !== null && !('error' in request) && type === 'prize-embargo' ? (
              <Box mt={2}>
                <List sx={{ padding: 0 }}>
                  <ListItem>
                    <PrizeAssignedEmbargo item={request as EmbargoDetails} marketName={request.request.market.name} />
                  </ListItem>
                </List>
              </Box>
            ) : null}
          </DialogContent>
          <DialogActions>
            {!!request && !('error' in request) && 'request' in request && (
              <>
                <Button
                  disabled={isDisabledButtons}
                  variant="outlined"
                  color="secondary"
                  onClick={() => sendTestEmails()}
                >
                  {t('actions.testEmails')}
                </Button>
                {request.request.testEmailDate && (
                  <Button
                    disabled={
                      isDisabledButtons || !!request.request.sendSampleDate || !!request.request.sendAllDataDate
                    }
                    variant="outlined"
                    color="info"
                    onClick={() => sendSampleEmail()}
                  >
                    {t('actions.sampleEmail')}
                  </Button>
                )}
                {request.request.sendSampleDate && (
                  <Button
                    disabled={isDisabledButtons || !!request.request.sendAllDataDate}
                    variant="contained"
                    color="success"
                    onClick={() => sendFinishEmails()}
                  >
                    {t('actions.finishEmails')}
                  </Button>
                )}
                {!request.request.sendSampleDate && (
                  <RejectButton type={type} requestId={requestId} disabled={isDisabledButtons} />
                )}
              </>
            )}
            <Button disabled={isDisabledButtons} onClick={close} variant="text" color="error">
              {t('actions.cancel')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  )
}
